import { Fragment, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useComplianceScope } from '@field-tickets/shared';
import { Spinner } from '@/components/Spinner';
import { useTicketSessionKey } from '@/hooks/useTicketSessionKey';
import { useIntentionalSignOut } from '@/hooks/useIntentionalSignOut';
import { retryComplianceSession, useComplianceSessionEnded } from '@/utils/complianceSession';

/**
 * The compliance register (#1011) is gated like Chat: `GET /compliance/scope`
 * answers 404 while the flag is off (data null). `read` needs can_read in at
 * least one company; `self` needs a linked employee. The server re-checks.
 */
function RequireCompliance({ need, children }: { need: 'read' | 'self'; children: ReactNode }) {
  const scope = useComplianceScope();
  if (scope.isLoading && !scope.data) {
    return (
      <div className="page-loading">
        <Spinner />
      </div>
    );
  }
  const allowed = need === 'read'
    ? scope.data?.companies.some((c) => c.can_read) === true
    : scope.data?.self != null;
  if (!allowed) {
    return <Navigate to="/no-access" replace />;
  }
  return <>{children}</>;
}

/** Shown after a register 401: nothing private, and no automatic retry. */
function ComplianceSessionEnded() {
  const signOut = useIntentionalSignOut();
  return (
    <div className="empty-state" role="alert">
      {signOut.dialog}
      <div className="empty-state__title">Your session ended — sign in again</div>
      <div className="empty-state__description">These documents are hidden until you sign in.</div>
      <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center', marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={signOut.isPending}
          onClick={() => { void signOut.requestSignOut(); }}
        >
          Sign in
        </button>
        <button type="button" className="btn btn-secondary" onClick={retryComplianceSession}>
          Retry
        </button>
      </div>
    </div>
  );
}

/**
 * A compliance route (Safety, My documents). Keyed by the signed-in owner and
 * session epoch, like the ticket editors: a sign-out, expiry or user switch
 * unmounts the whole subtree, scope gate included, so no mounted query
 * observer keeps showing the previous person's documents and no placeholder
 * data crosses to the next person. A 401 from the current session's register
 * requests replaces it with "session ended" until the identity changes or
 * the user retries. The cache itself is cleared by
 * installComplianceSessionBoundary.
 */
export function ComplianceRoute({ need, children }: { need: 'read' | 'self'; children: ReactNode }) {
  const sessionKey = useTicketSessionKey();
  const sessionEnded = useComplianceSessionEnded();
  if (sessionEnded) return <ComplianceSessionEnded />;
  return (
    <Fragment key={sessionKey}>
      <RequireCompliance need={need}>{children}</RequireCompliance>
    </Fragment>
  );
}
